// Vue 3 object creation procedure
import {createApp} from 'vue';
import {createRouter, createWebHistory} from 'vue-router';
import {createI18n} from 'vue-i18n';
import {messages, defaultLocale} from './messages.js';
import {createStore} from 'vuex';
import App from './components/App.vue';
import Dashboard from './components/Dashboard.vue';
import Login from './components/Login.vue';
import CuadroMando from './components/CuadroMando.vue';
import CuadroMandoRed from './components/CuadroMandoRed.vue';
import CuadroMandoIT from './components/CuadroMandoIT.vue';
import BootstrapVueNext from 'bootstrap-vue-next';
import permisosUser from './components/permisosUser.vue';
import trashBin from "./components/PapeleraReciclaje.vue";
import subscripciones from "./components/Suscriptores.vue";
import subscripcion from "./components/PanelSuscripcionUsuario.vue";
import panelErrores from "./components/PanelErrores.vue";
import buscadorIncidencias from "./components/BuscadorIncidencias.vue";
import EnvioSms from "./components/EnvioSms.vue";
import ListasDestinatarios from "./components/ListasDestinatarios.vue";
import auth from './auth.js';

// Bootstrap 5 requires only one import in index.js
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';
import './css/bootstrap2.min.css';
import './main.scss';
import './css/theme.css';
import './scss/custom.scss';
import './fonts/fontawesome-all.min.css';
import cuadroMando from "./components/CuadroMando.vue";

// create an instance using the function, Vue3
const app = createApp(App);

// Launch BootstrapVueNext
app.use(BootstrapVueNext);

export const store = createStore({
        state() {
            return {
                fotoAvatar: '',
                busquedaActivada: 0,
                benchmark: {},
                editar: false,
                botonEditar: true,
                lanzadoCronGeneral: false,
                lanzadoCronRed: false,
                lanzadoCronIT: false,
                resumenAbierto: true,
                veResumen: false
            };
        },
        mutations: {
            toggleResumen(state) {
                state.resumenAbierto = !state.resumenAbierto
            }
        }
    }
);

// Create an instance of VueI18n using createI18n
const i18n = createI18n({
    locale: defaultLocale, // default locale
    messages,
});

// Creating Vue 3 routes
const routes = [
    {
        path: '/',
        redirect: '/login'
    },
    {
        path: '/login',
        name: 'login',
        component: Login
    },
    {
        name: 'dashboard',
        path: '/dashboard',
        component: Dashboard,
        children: [
            {
                path: '/cuadroMando',
                name: 'cuadroMando',
                component: CuadroMando
            },
            {
                path: '/cuadroMandoRed',
                name: 'cuadroMandoRed',
                component: CuadroMandoRed
            },
            {
                path: '/cuadroMandoIT',
                name: 'cuadroMandoIT',
                component: CuadroMandoIT
            },
            {
                path: '/permisosUser',
                name: 'permisosUser',
                component: permisosUser,
                beforeEnter: guardAdmin
            },
            {
                path: '/subscripciones',
                name: 'subscripciones',
                component: subscripciones
            },
            {
                path: '/subscripcion',
                name: 'subscripcion',
                component: subscripcion
            },
            {
                path: '/panelErrores',
                name: 'panelErrores',
                component: panelErrores
            },
            {
                path: '/buscadorIncidencias',
                name: 'buscadorIncidencias',
                component: buscadorIncidencias
            },
            {
                path: '/trashBin',
                name: 'trashBin',
                component: trashBin,
                beforeEnter: guardEditor
            },
            {
                path: '/envioSms',
                name: 'envioSms',
                component: EnvioSms,
                beforeEnter: guardPermisoEnvio
            },
            {
                path: '/listasDestinatarios',
                name: 'listasDestinatarios',
                component: ListasDestinatarios,
                beforeEnter: guardPermisoListas
            },
            {
                path: '/envioSmsIt',
                redirect: { name: 'envioSms' }
            },
        ],
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/:pathMatch(.*)*',
        redirect: '/login'
    }
]

async function guardAdmin() {
    try {
        const response = await fetch('api/permisos', {
            method: 'GET',
            headers: auth.getAuthHeader()
        });
        const data = await response.json();
        if (!(data[0] && Number(data[0].admin) === 1)) {
            return { name: 'cuadroMando' };
        }
    } catch (error) {
        console.error('Error al comprobar el permiso de administración', error);
        return { name: 'cuadroMando' };
    }
}

async function guardMenu(entrada, aviso) {
    try {
        const response = await fetch('api/permisos', {
            method: 'GET',
            headers: auth.getAuthHeader()
        });
        const data = await response.json();
        const menus = data[0] && data[0].menus;
        if (!(menus && menus[entrada])) {
            return { name: 'cuadroMando' };
        }
    } catch (error) {
        console.error(aviso, error);
        return { name: 'cuadroMando' };
    }
}

async function guardEditor() {
    try {
        const response = await fetch('api/permisos', {
            method: 'GET',
            headers: auth.getAuthHeader()
        });
        const data = await response.json();
        if (!(data[0] && Number(data[0].editor) === 1)) {
            return { name: 'cuadroMando' };
        }
    } catch (error) {
        console.error('Error al comprobar el permiso de edición', error);
        return { name: 'cuadroMando' };
    }
}

function guardPermisoEnvio() {
    return guardMenu('envio', 'Error al comprobar el permiso de envío');
}

function guardPermisoListas() {
    return guardMenu('listas', 'Error al comprobar el permiso de listas');
}

// Creating Vue 3 Router
const router = createRouter({
    history: createWebHistory(),
    routes
});

// Authentication function (to be completed by you)
function isAuthenticated() {
    return localStorage.getItem("id_token") ?? false;
}

// Global navigation guard
router.beforeEach((to, from, next) => {
    const token = isAuthenticated();
    if (to.matched.some(record => record.meta.requiresAuth) && !token) {
        next('/login');
    } else if (to.name === 'login' && token) {
        next('/cuadroMando');
    } else {
        next();
    }
});

// Vue 3 mounting app component
app.use(store);
app.use(i18n);
app.use(router).mount('#app');

